import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { toast } from "sonner";
import { FaWhatsapp, FaInstagram, FaEnvelope, FaCheck, FaTimes, FaLightbulb } from "react-icons/fa";
import "./UpdatesPage.css";

function UpdatesPage() {
  const userId = localStorage.getItem("userId");
  const isLoggedIn = !!userId;

  const [updates, setUpdates] = useState([
    { id: "1", title: "Canlı Dersler", description: "Eğitimcilerle canlı dersler yakında!", date: "Mayıs 2025", votesYes: 0, votesNo: 0, voters: [], userVote: null },
    { id: "2", title: "Yeni Etüt Sistemi", description: "Etüt arkadaşlarınızla daha etkili çalışın.", date: "Haziran 2025", votesYes: 0, votesNo: 0, voters: [], userVote: null },
    { id: "3", title: "Mobil Uygulama", description: "Yelken artık mobil cihazlarınızda!", date: "Temmuz 2025", votesYes: 0, votesNo: 0, voters: [], userVote: null },
  ]);

  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState("suggestion");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [selectedUpdateTitle, setSelectedUpdateTitle] = useState(""); // Hangi güncellemeyle ilgili öneri yapıldığını tutar

  // Firebase'den oyları yükle
  useEffect(() => {
    const fetchVotes = async () => {
      const updatedUpdates = await Promise.all(
        updates.map(async (update) => {
          const voteRef = doc(db, "updateVotes", update.id);
          const voteSnap = await getDoc(voteRef);
          if (voteSnap.exists()) {
            const data = voteSnap.data();
            const userHasVoted = data.voters?.includes(userId);
            return {
              ...update,
              votesYes: data.votesYes || 0,
              votesNo: data.votesNo || 0,
              voters: data.voters || [],
              userVote: userHasVoted ? (data.votesYes > update.votesYes ? "yes" : "no") : null,
            };
          }
          return update;
        })
      );
      setUpdates(updatedUpdates);
    };
    fetchVotes();
  }, [userId]);

  const handleVote = async (updateId, voteType) => {
    if (!isLoggedIn) {
      toast.error("Oy kullanmak için giriş yapmalısınız!");
      return;
    }

    const updateIndex = updates.findIndex((update) => update.id === updateId);
    const update = updates[updateIndex];

    try {
      const voteRef = doc(db, "updateVotes", updateId);
      const voteSnap = await getDoc(voteRef);

      let newVotesYes = update.votesYes;
      let newVotesNo = update.votesNo;
      let newVoters = [...update.voters];
      let newUserVote = update.userVote;

      // Kullanıcı daha önce oy vermiş mi ve aynı butona mı tıklıyor?
      if (update.voters.includes(userId) && update.userVote === voteType) {
        // Oyu geri al
        if (voteType === "yes") {
          newVotesYes -= 1;
        } else {
          newVotesNo -= 1;
        }
        newVoters = newVoters.filter((voter) => voter !== userId);
        newUserVote = null;
        toast.success("Oyun geri alındı!");
      } else if (update.voters.includes(userId)) {
        // Kullanıcı diğer seçeneğe oy vermiş, oyu değiştir
        if (voteType === "yes") {
          newVotesYes += 1;
          newVotesNo -= 1;
          newUserVote = "yes";
        } else {
          newVotesNo += 1;
          newVotesYes -= 1;
          newUserVote = "no";
        }
        toast.success(`${voteType === "yes" ? "Gelsin" : "Gelmesin"} oyu verdiniz! 🎉`);
      } else {
        // Yeni oy
        if (voteType === "yes") {
          newVotesYes += 1;
        } else {
          newVotesNo += 1;
        }
        newVoters.push(userId);
        newUserVote = voteType;
        toast.success(`${voteType === "yes" ? "Gelsin" : "Gelmesin"} oyu verdiniz! 🎉`);
      }

      // Firebase'e oyları kaydet
      if (voteSnap.exists()) {
        await updateDoc(voteRef, {
          votesYes: newVotesYes,
          votesNo: newVotesNo,
          voters: newVoters,
        });
      } else {
        await setDoc(voteRef, {
          votesYes: newVotesYes,
          votesNo: newVotesNo,
          voters: newVoters,
        });
      }

      // State'i güncelle
      const updatedUpdates = [...updates];
      updatedUpdates[updateIndex] = {
        ...update,
        votesYes: newVotesYes,
        votesNo: newVotesNo,
        voters: newVoters,
        userVote: newUserVote,
      };
      setUpdates(updatedUpdates);
    } catch (err) {
      console.error("Oy kullanılırken hata oluştu:", err);
      toast.error("Oy kullanılırken bir hata oluştu.");
    }
  };

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess(false);

    if (!title.trim() || !description.trim()) {
      setError("Lütfen tüm alanları doldurun.");
      return;
    }

    try {
      const feedbackData = {
        userId: userId || "anonymous",
        username: localStorage.getItem("username") || "anonymous",
        type: feedbackType,
        title: title.trim(),
        description: description.trim(),
        timestamp: new Date(),
        status: "pending",
      };

      await setDoc(doc(db, "feedbacks", `${userId || "anonymous"}-${Date.now()}`), feedbackData);

      setSuccess(true);
      setTitle("");
      setDescription("");
      setFeedbackType("suggestion");
      setSelectedUpdateTitle("");

      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error("Geri bildirim gönderilirken hata oluştu:", err);
      setError("Geri bildirim gönderilirken bir hata oluştu. Lütfen tekrar deneyin.");
    }
  };

  const openFeedbackModal = (updateTitle = "") => {
    setSelectedUpdateTitle(updateTitle);
    setTitle(updateTitle ? `Öneri: ${updateTitle}` : "");
    setFeedbackOpen(true);
  };

  return (
    <motion.div
      style={containerStyle}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <motion.div
        style={updatesContainerStyle}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.5 }}
      >
        <motion.h2
          style={updatesHeaderStyle}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          Gelecek Güncellemeler ve Özellikler
        </motion.h2>
        <motion.div
          style={updatesTimelineStyle}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          <AnimatePresence>
            {updates.map((update, index) => (
              <motion.div
                key={update.id}
                style={updateItemStyle}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ delay: index * 0.1, duration: 0.3 }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div style={timelineDotStyle}></div>
                <h3 style={updateTitleStyle}>{update.title}</h3>
                <p style={updateDescriptionStyle}>{update.description}</p>
                <span style={updateDateStyle}>Tahmini Yayın: {update.date}</span>
                <div style={voteContainerStyle}>
                  <motion.button
                    className={`vote-button ${update.userVote === "yes" ? "voted" : ""}`}
                    onClick={() => handleVote(update.id, "yes")}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                  >
                    <FaCheck style={{ marginRight: "5px" }} /> Gelsin ({update.votesYes})
                  </motion.button>
                  <motion.button
                    className={`vote-button ${update.userVote === "no" ? "voted" : ""}`}
                    onClick={() => handleVote(update.id, "no")}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                  >
                    <FaTimes style={{ marginRight: "5px" }} /> Gelmesin ({update.votesNo})
                  </motion.button>
                  <motion.button
                    className="suggest-button"
                    onClick={() => openFeedbackModal(update.title)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                  >
                    <FaLightbulb style={{ marginRight: "5px" }} /> Nasıl Geliştirebiliriz?
                  </motion.button>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
        <motion.div
          style={feedbackSectionStyle}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.5 }}
        >
          <h3 style={feedbackTitleStyle}>Görüşlerinizi Paylaşın!</h3>
          <p style={feedbackTextStyle}>
            Gelecek güncellemeler hakkında ne düşünüyorsunuz? Geri bildirimlerinizi bizimle paylaşın! Ayrıca, yeni fikirleriniz varsa bunları da bizimle paylaşabilirsiniz.
          </p>
          <motion.button
            style={feedbackButtonStyle}
            onClick={() => openFeedbackModal()}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            transition={{ duration: 0.2 }}
          >
            Geri Bildirim Gönder
          </motion.button>
        </motion.div>
      </motion.div>

      {/* Geri Bildirim Modal */}
      <AnimatePresence>
        {feedbackOpen && (
          <motion.div
            className="feedback-modal"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            transition={{ duration: 0.3 }}
          >
            <motion.div
              className="feedback-modal-content"
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.8 }}
              transition={{ duration: 0.3 }}
            >
              <motion.button
                className="feedback-modal-close"
                onClick={() => setFeedbackOpen(false)}
                whileHover={{ scale: 1.2, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
              >
                ✕
              </motion.button>

              <motion.p
                className="feedback-intro"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.5 }}
              >
                Görüşleriniz bizim için çok değerli! Güncellemeler hakkında önerilerinizi, yeni fikirlerinizi veya genel yorumlarınızı paylaşabilirsiniz.
              </motion.p>

              <AnimatePresence>
                {success && (
                  <motion.div
                    className="feedback-success-message"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.3 }}
                  >
                    🎉 Geri bildiriminiz başarıyla gönderildi! Teşekkür ederiz.
                  </motion.div>
                )}
              </AnimatePresence>

              <AnimatePresence>
                {error && (
                  <motion.div
                    className="feedback-error-message"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.3 }}
                  >
                    {error}
                  </motion.div>
                )}
              </AnimatePresence>

              <motion.form
                onSubmit={handleFeedbackSubmit}
                className="feedback-form"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.5 }}
              >
                <motion.div className="feedback-form-group">
                  <label htmlFor="feedbackType" className="feedback-label">Geri Bildirim Türü:</label>
                  <motion.select
                    id="feedbackType"
                    value={feedbackType}
                    onChange={(e) => setFeedbackType(e.target.value)}
                    className="feedback-select"
                    whileFocus={{ borderColor: "#00e676" }}
                    transition={{ duration: 0.2 }}
                  >
                    <option value="suggestion">Öneri</option>
                    <option value="bug">Hata Bildirimi</option>
                    <option value="general">Genel Yorum</option>
                  </motion.select>
                </motion.div>

                <motion.div className="feedback-form-group">
                  <label htmlFor="title" className="feedback-label">Başlık:</label>
                  <motion.input
                    type="text"
                    id="title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Geri bildiriminizin başlığını yazın..."
                    className="feedback-input"
                    whileFocus={{ borderColor: "#00e676" }}
                    transition={{ duration: 0.2 }}
                  />
                </motion.div>

                <motion.div className="feedback-form-group">
                  <label htmlFor="description" className="feedback-label">Açıklama:</label>
                  <motion.textarea
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Detaylı açıklamanızı buraya yazın..."
                    className="feedback-textarea"
                    whileFocus={{ borderColor: "#00e676" }}
                    transition={{ duration: 0.2 }}
                  />
                </motion.div>

                <motion.button
                  type="submit"
                  className="feedback-submit-button"
                  whileHover={{ scale: 1.05, backgroundColor: "#FFB300" }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                >
                  Gönder
                </motion.button>
              </motion.form>

              <motion.div
                className="feedback-social-icons"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.5 }}
              >
                <motion.a
                  href="https://wa.me/+905442256334"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="feedback-social-icon whatsapp"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  transition={{ duration: 0.2 }}
                >
                  <FaWhatsapp />
                </motion.a>
                <motion.a
                  href="https://www.instagram.com/yelken.pro/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="feedback-social-icon instagram"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  transition={{ duration: 0.2 }}
                >
                  <FaInstagram />
                </motion.a>
                <motion.a
                  href="mailto:destek@yelken.pro"
                  className="feedback-social-icon gmail"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  transition={{ duration: 0.2 }}
                >
                  <FaEnvelope />
                </motion.a>
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

const containerStyle = {
  background: "linear-gradient(135deg, #0f2027, #203a43, #2c5364)",
  minHeight: "100vh",
  padding: "40px",
  fontFamily: "'Inter', sans-serif",
  color: "#f0f0f0",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  position: "relative",
  overflow: "hidden",
};

const updatesContainerStyle = {
  maxWidth: "800px",
  width: "100%",
  background: "rgba(255, 255, 255, 0.05)",
  borderRadius: "16px",
  padding: "30px",
  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.5)",
  border: "1px solid rgba(0, 230, 118, 0.2)",
  position: "relative",
  zIndex: 1,
};

const updatesHeaderStyle = {
  fontSize: "32px",
  textAlign: "center",
  marginBottom: "30px",
  color: "#00ffcc",
  textShadow: "0 0 10px rgba(0, 230, 118, 0.3)",
};

const updatesTimelineStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "40px",
  position: "relative",
};

const updateItemStyle = {
  background: "rgba(255, 255, 255, 0.05)",
  padding: "20px",
  borderRadius: "12px",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.5)",
  border: "1px solid rgba(0, 230, 118, 0.2)",
  position: "relative",
};

const timelineDotStyle = {
  width: "12px",
  height: "12px",
  background: "#00ffcc",
  borderRadius: "50%",
  position: "absolute",
  left: "-30px",
  top: "20px",
  boxShadow: "0 0 8px rgba(0, 255, 204, 0.5)",
};

const updateTitleStyle = {
  fontSize: "20px",
  color: "#00ffcc",
  marginBottom: "8px",
};

const updateDescriptionStyle = {
  fontSize: "16px",
  color: "#f0f0f0",
  marginBottom: "8px",
};

const updateDateStyle = {
  fontSize: "14px",
  color: "#b0bec5",
};

const voteContainerStyle = {
  display: "flex",
  gap: "10px",
  marginTop: "10px",
  flexWrap: "wrap",
};

const feedbackSectionStyle = {
  marginTop: "40px",
  textAlign: "center",
};

const feedbackTitleStyle = {
  fontSize: "24px",
  color: "#00ffcc",
  textShadow: "0 0 8px rgba(0, 230, 118, 0.3)",
  marginBottom: "10px",
};

const feedbackTextStyle = {
  fontSize: "16px",
  color: "#b0bec5",
  marginBottom: "20px",
};

const feedbackButtonStyle = {
  padding: "12px 24px",
  background: "linear-gradient(135deg, #00e676, #00c853)",
  border: "none",
  borderRadius: "8px",
  cursor: "pointer",
  fontSize: "16px",
  fontWeight: "bold",
  color: "#fff",
};

export default UpdatesPage;